import _ from 'lodash';
import { Ty } from '../ty';
import { Assert, Freeze } from '../utils';
import { narrowestSuperTypeOf } from '../type-checker/implements';
import { PRIMITIVE_TYPE_INFERENCE_RULES } from './primitive-type-inference-rules';
export {
  validatorForType,
  jsonSchemaForType,
} from './primitive-type-inference-rules';

const ENUM_INFERENCE_VARIANT_NUMBER = 16;
const ENUM_INFERENCE_VARIANT_MAX_LENGTH = 32;

/**
 * Infers the most specific type from an array of scalar values.
 *
 * This function analyzes a sample of values and returns the narrowest type that
 * can represent all of them. It attempts type inference in the following order:
 * 1. String enums (if unique values are within configurable limits)
 * 2. Plain strings
 * 3. Nested structs (recursively infers field types)
 * 4. Integer ranges (if all numbers are integers)
 * 5. Primitive types (boolean, date, timestamp, etc.)
 * 6. Arrays (recursively infers element type and finds common supertype)
 *
 * @param values - Array of scalar values to analyze for type inference
 * @param opts - Optional configuration for enum inference
 * @param opts.maxVariantNumber - Maximum number of unique string values to infer as enum (default: 16)
 * @param opts.maxVariantLength - Maximum length of each enum variant string (default: 32)
 *
 * @returns The inferred extended attribute type, or undefined if:
 *   - The input array is empty
 *   - All values are null
 *   - No suitable type can be inferred (e.g., mixed incompatible types)
 *   - Recursive inference fails for nested structures
 *
 * @remarks
 * - Nullable types are automatically detected based on the presence of null values
 * - For enums, values are deduplicated and sorted alphabetically
 * - For structs, all unique fields across all objects are merged and inferred recursively
 * - For arrays, the narrowest common supertype of all element types is computed
 * - Integer ranges capture the min/max values from the sample
 * - To disable enum inference and always infer strings as plain string type,
 *   pass `{ maxVariantNumber: 0 }` or `{ maxVariantLength: 0 }` in opts
 */
export const inferType = (
  values: Ty.Scalar[],
  opts?: {
    maxVariantNumber?: number;
    maxVariantLength?: number;
  }
): Ty.ExtendedAttributeType | undefined => {
  const maxVariantNumber =
    opts?.maxVariantNumber ?? ENUM_INFERENCE_VARIANT_NUMBER;
  const maxVariantLength =
    opts?.maxVariantLength ?? ENUM_INFERENCE_VARIANT_MAX_LENGTH;

  if (values.length === 0) {
    return undefined;
  }

  if (values.every((n) => n === null)) {
    return { ty: { k: 'primitive', t: 'super' }, nullable: true, tags: [] };
  }

  const nullable = values.some((val) => val === null);

  if (values.every((val) => typeof val === 'string' || val === null)) {
    const uniqStrs = Object.freeze(
      _.uniq(values.filter((val) => typeof val === 'string')).sort()
    ) as readonly string[];

    if (
      uniqStrs.length > 0 &&
      uniqStrs.length <= maxVariantNumber &&
      uniqStrs.every((variant) => variant.length <= maxVariantLength)
    ) {
      return { ty: { k: 'enum', t: uniqStrs }, nullable, tags: [] };
    } else {
      return { ty: { k: 'primitive', t: 'string' }, nullable, tags: [] };
    }
  }

  if (
    values.every(
      (val) =>
        typeof val === 'object' &&
        !(val instanceof Date) &&
        !Freeze.isReadonlyArray(val)
    )
  ) {
    const uniqFields = new Set(values.flatMap((val) => Object.keys(val ?? {})));

    const inference = [...uniqFields].map(
      (field) =>
        [
          field,
          inferType(
            values.map((val) =>
              typeof val === 'object' &&
              !(val instanceof Date) &&
              !Freeze.isReadonlyArray(val)
                ? val?.[field] ?? null
                : null
            ),
            opts
          ) ?? null,
        ] as const
    );

    if (inference.some(([_field, inference]) => inference === null)) {
      return undefined;
    }

    const fields = Object.fromEntries(
      _.compact(
        inference.map(([name, ty]) => (ty === null ? null : [name, ty]))
      )
    );

    return { ty: { k: 'struct', fields }, nullable, tags: [] };
  }

  if (isNumberArray(values)) {
    const nums = values.filter((x) => x !== null);
    if (nums.every((num) => Number.isInteger(num))) {
      const start = _.min(nums)!;
      const end = _.max(nums)!;
      return { ty: { k: 'range', start, end, t: 'int' }, nullable, tags: [] };
    }
  }

  for (const [t, validator] of PRIMITIVE_TYPE_INFERENCE_RULES) {
    if (validator.nullable().array().safeParse(values).success) {
      return { ty: { k: 'primitive', t }, nullable, tags: [] };
    }
  }

  if (values.every((val) => Array.isArray(val) || val === null)) {
    const inferred = values
      .filter((val) => val !== null)
      .map((val) => {
        Assert.assert(Array.isArray(val));
        return inferType(val, opts);
      });

    if (inferred.some((inference) => inference === undefined)) {
      return undefined;
    }

    const common = narrowestSuperTypeOf(_.compact(inferred)).unwrapOr(
      undefined
    );
    return common
      ? { nullable, ty: { k: 'array', t: common }, tags: common.tags }
      : common;
  }

  return undefined;
};

const isNumberArray = (x: any[]): x is (number | null)[] =>
  x.every((y) => typeof y === 'number' || y === null);
